// @ubm/product-ui/blocks/week-calendar — calendrier civil dans un fuseau IANA, PUR (Intl seulement). Les évènements sont des
// INSTANTS ; la grille les affiche dans `timeZone` (ou le fuseau du navigateur). Une date civile est une clé "YYYY-MM-DD" ;
// l'arithmétique de jours se fait en UTC sur ces clés (aucun décalage horaire n'y intervient).

export interface ZonedParts { year: number; month: number; day: number; hour: number; minute: number }

const formatters = new Map<string, Intl.DateTimeFormat>();
function formatter(timeZone: string | undefined) {
  const key = timeZone ?? "";
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
    formatters.set(key, f);
  }
  return f;
}

/** Wall-clock parts of an instant in a zone. */
export function zonedParts(date: Date, timeZone?: string): ZonedParts {
  const parts = formatter(timeZone).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find(p => p.type === type)?.value ?? 0);
  return { year: get("year"), month: get("month"), day: get("day"), hour: get("hour") % 24, minute: get("minute") };
}

/** The instant of a wall-clock time in a zone (the earlier one across a fall-back; the shifted one across a gap). */
export function zonedInstant(year: number, month: number, day: number, hour: number, minute: number, timeZone?: string): Date {
  const target = Date.UTC(year, month - 1, day, hour, minute);
  const wall = (instant: number) => {
    const p = zonedParts(new Date(instant), timeZone);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
  };
  // Collect offsets on either side of a transition. Matching candidates choose the
  // earlier fold; a gap shifts forward by its width (compatible disambiguation).
  const offsets = new Set([-2, -1, 0, 1, 2].map(days => {
    const instant = target + days * 86400000;
    return wall(instant) - instant;
  }));
  const candidates = [...offsets].map(offset => ({ instant: target - offset, civil: wall(target - offset) }));
  const matches = candidates.filter(candidate => candidate.civil === target);
  if (matches.length) return new Date(Math.min(...matches.map(candidate => candidate.instant)));
  const forward = candidates.filter(candidate => candidate.civil > target).sort((a, b) => a.civil - b.civil);
  return new Date(forward[0]?.instant ?? candidates[0]!.instant);
}

export const pad = (n: number) => String(n).padStart(2, "0");
export const dayKey = (p: { year: number; month: number; day: number }) => `${p.year}-${pad(p.month)}-${pad(p.day)}`;
export function parseKey(key: string): { year: number; month: number; day: number } {
  const [y, m, d] = key.split("-").map(Number);
  return { year: y!, month: m!, day: d! };
}
/** Civil date key shifted by whole days. */
export function addDaysKey(key: string, days: number): string {
  const { year, month, day } = parseKey(key);
  const t = new Date(Date.UTC(year, month - 1, day + days));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}
/** 0 = Sunday … 6 = Saturday, for a civil date. */
export function weekdayOf(key: string): number {
  const { year, month, day } = parseKey(key);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}
export function diffDays(a: string, b: string): number {
  const pa = parseKey(a), pb = parseKey(b);
  return Math.round((Date.UTC(pb.year, pb.month - 1, pb.day) - Date.UTC(pa.year, pa.month - 1, pa.day)) / 86400000);
}
/** A civil date as a noon UTC Date — safe to format with `timeZone: "UTC"` without drifting a day. */
export const keyAsDate = (key: string) => { const { year, month, day } = parseKey(key); return new Date(Date.UTC(year, month - 1, day, 12)); };
/** A civil date as a LOCAL Date (for the Calendar canon, which reads local civil dates). */
export const keyAsLocalDate = (key: string) => { const { year, month, day } = parseKey(key); return new Date(year, month - 1, day); };
export const localDateKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
